import type { StyleRun } from '@rta/rta-blueprint-library';

import { STYLE_RUN_PROPERTIES } from './apply-style-run';

/** The run-level overrides in force at a point or uniformly across a range; an absent property inherits the box-level scalar */
export type EffectiveTextStyle = Omit<StyleRun, 'start' | 'end'>;

/** Strips the offsets off a run, leaving only the properties it actually overrides */
export const toEffectiveStyle = (run: StyleRun): EffectiveTextStyle => {
	const style: EffectiveTextStyle = {};
	const target: Record<string, unknown> = style;
	STYLE_RUN_PROPERTIES.forEach((property) => {
		const value = run[property];
		if (typeof value !== 'undefined') target[property] = value;
	});
	return style;
};

export const getStyleAtOffset = (runs: Array<StyleRun>, offset: number): EffectiveTextStyle => {
	const run = runs.find((candidate) => offset >= candidate.start && offset < candidate.end);
	return run ? toEffectiveStyle(run) : {};
};

const intersectStyles = (first: EffectiveTextStyle, second: EffectiveTextStyle): EffectiveTextStyle => {
	const result: EffectiveTextStyle = {};
	const target: Record<string, unknown> = result;
	STYLE_RUN_PROPERTIES.forEach((property) => {
		const value = first[property];
		if (typeof value !== 'undefined' && value === second[property]) target[property] = value;
	});
	return result;
};

export type GetRangeStyleParams = {
	runs: Array<StyleRun>;
	start: number;
	end: number;
};

/**
 * The style a toolbar should show as active for a selection: only properties identical for every character
 * in [start, end) are returned, so a mixed or partly-unset property is omitted and the control falls back
 * to the box-level scalar.
 */
export const getRangeStyle = (params: GetRangeStyleParams): EffectiveTextStyle => {
	const { runs, start, end } = params;
	if (end <= start) return getStyleAtOffset(runs, start);

	let result = getStyleAtOffset(runs, start);
	for (let offset = start + 1; offset < end; offset += 1) {
		result = intersectStyles(result, getStyleAtOffset(runs, offset));
	}
	return result;
};
